import SectionHeader from "@/components/SectionHeader";

const FOREST = "https://customer-assets.emergentagent.com/job_green-workforce-1/artifacts/su2qw556_desert-fire-jeep.jpeg";
const OUTBACK = "https://images.unsplash.com/photo-1666079368573-182459ed245b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxBdXN0cmFsaWFuJTIwb3V0YmFjayUyMGxhbmRzY2FwZSUyMG9jaHJlJTIwZXVjYWx5cHR8ZW58MHx8fHwxNzgwMzEwODM0fDA&ixlib=rb-4.1.0&q=85";

export default function AboutPage() {
  return (
    <div data-testid="page-about">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeader
          overline="About"
          title={<>An independent platform for <span className="italic text-ra-ochre">restoration</span> at scale.</>}
          lede="Restoration Australia is an independent platform exploring the future of ecological restoration, Caring for Country and community resilience."
          testId="about-header"
        />
        <div className="mt-10 max-w-3xl space-y-5 text-ra-eucalypt leading-relaxed text-lg">
          <p>We believe successful restoration requires more than on-ground projects.</p>
          <p className="text-ra-soft">
            It requires strong communities, Indigenous leadership, resilient seed systems,
            knowledge sharing, long-term investment and practical pathways that connect
            environmental outcomes with social and economic benefits.
          </p>
          <p className="text-ra-soft">
            Our role is to share ideas, highlight innovation, amplify successful models and
            contribute to the national conversation about how Australia restores Country at scale.
          </p>
        </div>
      </section>

      <section className="bg-ra-paper border-y border-ra-line">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-12 gap-12 items-center">
          <div className="col-span-12 lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-ra-line">
              <img src={FOREST} alt="Restoration on Country" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="ra-overline">Our purpose</div>
            <h2 className="ra-serif text-4xl lg:text-5xl mt-4 leading-[1.05]">
              Supporting the <span className="italic text-ra-ochre">restoration economy</span>.
            </h2>
            <p className="mt-6 text-lg text-ra-eucalypt leading-relaxed">
              Australia holds one of the most ancient, biologically distinct estates on Earth — and
              also one of the most modified. The work ahead is generational: re-stitching
              ecosystems, rebuilding native seed supply, and supporting the people doing the work.
            </p>
            <p className="mt-5 text-ra-soft leading-relaxed">
              We work across science, practice, policy and economy — connecting Traditional Owners,
              practitioners, researchers, landholders, agencies and industry to deliver durable
              recovery at landscape scale.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ra-line border border-ra-line">
          {[
            ["01", "Lead with Country", "We follow the leadership of Traditional Owners. Restoration on Country is restoration with culture, knowledge and self-determination at its heart."],
            ["02", "Practice over posture", "We invest where the work happens — in field practice, native seed supply, training, and the standards that make outcomes credible."],
            ["03", "Restoration economy", "We build the markets, methods and infrastructure required to move from compliance to outcomes — at scale, for the long term."],
          ].map(([n, title, body]) => (
            <div key={n} className="bg-ra-bone p-10 lg:p-12">
              <div className="ra-serif text-6xl text-ra-ochre">{n}</div>
              <h3 className="ra-serif text-3xl mt-5">{title}</h3>
              <p className="mt-4 text-ra-soft leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={OUTBACK} alt="Outback" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-ra-bone via-ra-bone/30 to-ra-bone"></div>
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center">
          <div className="ra-overline">A note on Country</div>
          <p className="ra-serif text-3xl lg:text-4xl mt-6 leading-[1.15] italic text-ra-eucalypt">
            "We acknowledge the Traditional Owners of the lands and waters across Australia and pay
            our respects to Elders past and present. Always was, always will be Aboriginal land."
          </p>
        </div>
      </section>
    </div>
  );
}
